import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 공사일지 검색 조건의 정규화·URL 변환·API 파라미터 변환을 한 곳에서 담당한다.
 * route 문자열 → 정규화 조건 → API params 순서로만 흐른다.
 * 정본: docs/bricks-milestone-2/api-contract.md §2, TRD §6.2~6.3
 */

export const KEYWORD_MAX_CODE_POINTS = 100;
export const FILTER_IDS_MAX = 20;
export const RECENT_DAYS_PRESET_LENGTH = 7;

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
const WHITESPACE_RUN_PATTERN = /\s+/gu;

export interface DailyLogSearchConditions {
  /** 정규화된 검색어. 빈 문자열이면 검색어 없음 */
  keyword: string;
  startDate: string | null;
  endDate: string | null;
  /** 오름차순·중복 제거 */
  contractorIds: number[];
  /** 오름차순·중복 제거 */
  tagIds: number[];
}

export const EMPTY_SEARCH_CONDITIONS: DailyLogSearchConditions = {
  keyword: "",
  startDate: null,
  endDate: null,
  contractorIds: [],
  tagIds: [],
};

/**
 * 프로젝트 route의 검색 관련 search param (URL 이름).
 * 숫자 하나뿐인 ID 목록은 number로 둬야 TanStack Router 기본 직렬화가 `contractors=%227%22`처럼 따옴표를 붙이지 않는다.
 */
export interface DailyLogSearchRouteParams {
  q?: string;
  from?: string;
  to?: string;
  contractors?: string | number;
  tags?: string | number;
}

export type DailyLogSearchRouteInput = DailyLogSearchRouteParams;

export type SearchConditionIssue =
  | "keyword-too-long"
  | "invalid-start-date"
  | "invalid-end-date"
  | "reversed-date-range"
  | "invalid-contractors"
  | "too-many-contractors"
  | "invalid-tags"
  | "too-many-tags";

export interface ParsedSearchConditions {
  /** 잘못된 필드는 기본값으로 비워 둔 조건. issues가 있으면 검색을 실행하지 않는다 */
  conditions: DailyLogSearchConditions;
  issues: SearchConditionIssue[];
}

export function countCodePoints(text: string): number {
  return Array.from(text).length;
}

/** NFC → 연속 공백·줄바꿈·NBSP를 한 칸으로 → 앞뒤 공백 제거 */
export function normalizeSearchKeyword(raw: string): string {
  return raw.normalize("NFC").replace(WHITESPACE_RUN_PATTERN, " ").trim();
}

/** 형식뿐 아니라 실제 존재하는 날짜인지 검사한다 (예: 2026-02-30 거부) */
export function isValidIsoDate(value: string): boolean {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

type IdListParseResult = { ok: true; ids: number[] } | { ok: false };

/** 쉼표 구분 양의 정수 목록. 빈 문자열은 빈 선택, 빈 중간 토큰·0·음수·비정수는 실패 */
export function parseIdList(raw: string): IdListParseResult {
  if (raw === "") return { ok: true, ids: [] };

  const tokens = raw.split(",");
  const isEveryTokenValid = tokens.every(
    (token) => POSITIVE_INTEGER_PATTERN.test(token) && Number.isSafeInteger(Number(token)),
  );
  if (!isEveryTokenValid) return { ok: false };

  return { ok: true, ids: normalizeIdList(tokens.map(Number)) };
}

export function normalizeIdList(ids: readonly number[]): number[] {
  return [...new Set(ids)].sort((a, b) => a - b);
}

export function parseSearchConditions(params: DailyLogSearchRouteParams): ParsedSearchConditions {
  const issues: SearchConditionIssue[] = [];
  const conditions: DailyLogSearchConditions = { ...EMPTY_SEARCH_CONDITIONS };

  const keyword = normalizeSearchKeyword(params.q ?? "");
  if (countCodePoints(keyword) > KEYWORD_MAX_CODE_POINTS) {
    issues.push("keyword-too-long");
  } else {
    conditions.keyword = keyword;
  }

  const startDate = params.from ?? null;
  const endDate = params.to ?? null;
  const isStartDateValid = startDate === null || isValidIsoDate(startDate);
  const isEndDateValid = endDate === null || isValidIsoDate(endDate);
  if (!isStartDateValid) issues.push("invalid-start-date");
  if (!isEndDateValid) issues.push("invalid-end-date");

  if (isStartDateValid && isEndDateValid) {
    const isReversed = startDate !== null && endDate !== null && startDate > endDate;
    if (isReversed) {
      issues.push("reversed-date-range");
    } else {
      conditions.startDate = startDate;
      conditions.endDate = endDate;
    }
  }

  const contractors = parseIdList(String(params.contractors ?? ""));
  if (!contractors.ok) {
    issues.push("invalid-contractors");
  } else if (contractors.ids.length > FILTER_IDS_MAX) {
    issues.push("too-many-contractors");
  } else {
    conditions.contractorIds = contractors.ids;
  }

  const tags = parseIdList(String(params.tags ?? ""));
  if (!tags.ok) {
    issues.push("invalid-tags");
  } else if (tags.ids.length > FILTER_IDS_MAX) {
    issues.push("too-many-tags");
  } else {
    conditions.tagIds = tags.ids;
  }

  return { conditions, issues };
}

function toRouteIdList(ids: readonly number[]): string | number | undefined {
  if (ids.length === 0) return undefined;
  if (ids.length === 1) return ids[0];
  return normalizeIdList(ids).join(",");
}

/** 기본값(빈 값)은 URL에서 생략한다 */
export function toRouteSearch(conditions: DailyLogSearchConditions): DailyLogSearchRouteInput {
  return {
    q: conditions.keyword || undefined,
    from: conditions.startDate ?? undefined,
    to: conditions.endDate ?? undefined,
    contractors: toRouteIdList(conditions.contractorIds),
    tags: toRouteIdList(conditions.tagIds),
  };
}

export type DailyLogSearchApiParams = {
  keyword?: string;
  start_date?: string;
  end_date?: string;
  contractor_ids?: string;
  tag_ids?: string;
  limit: number;
  cursor?: string;
};

/** API는 알 수 없는 파라미터·빈 배열 문법(`contractor_ids[]`)을 400으로 거부하므로 값이 있는 키만 보낸다 */
export function toApiParams(
  conditions: DailyLogSearchConditions,
  limit: number,
  cursor: string | null,
): DailyLogSearchApiParams {
  const params: DailyLogSearchApiParams = { limit };
  if (conditions.keyword) params.keyword = conditions.keyword;
  if (conditions.startDate) params.start_date = conditions.startDate;
  if (conditions.endDate) params.end_date = conditions.endDate;
  if (conditions.contractorIds.length > 0) params.contractor_ids = conditions.contractorIds.join(",");
  if (conditions.tagIds.length > 0) params.tag_ids = conditions.tagIds.join(",");
  if (cursor) params.cursor = cursor;
  return params;
}

export function hasAppliedConditions(conditions: DailyLogSearchConditions): boolean {
  return (
    conditions.keyword !== "" ||
    conditions.startDate !== null ||
    conditions.endDate !== null ||
    conditions.contractorIds.length > 0 ||
    conditions.tagIds.length > 0
  );
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

/** 프로젝트 timezone 기준 오늘. timezone이 잘못됐으면 null — 사용자 PC timezone으로 대체하지 않는다 */
export function getProjectToday(projectTimezone: string, now: Date = new Date()): string | null {
  try {
    return dayjs(now).tz(projectTimezone).format("YYYY-MM-DD");
  } catch {
    return null;
  }
}

/** 오늘 포함 최근 7일. 예: 오늘이 9/23이면 9/17~9/23 */
export function getRecentDaysRange(today: string): DateRange {
  // 날짜 문자열만 UTC로 계산해 DST 전환에 영향받지 않는다.
  const startDate = dayjs
    .utc(today)
    .subtract(RECENT_DAYS_PRESET_LENGTH - 1, "day")
    .format("YYYY-MM-DD");
  return { startDate, endDate: today };
}

/** 이번 달 1일~오늘. 미래 날짜는 포함하지 않는다 */
export function getCurrentMonthRange(today: string): DateRange {
  return { startDate: `${today.slice(0, 7)}-01`, endDate: today };
}
